import React, { useState } from "react";
import { Row, Col } from "react-bootstrap";
import { Button } from "../../Common";
import Input from "../../Common/V2/Widgets/Input";
import styles from "./Customer.module.css";
import { updateCustomer } from "../../api";
import CommentModal from "../../Common/CommentModal/commentModal";
import DropDown from "../../Common/DropDown/index";
import Modal from "../../Common/ClosureModal/Modal";
import { usTaxPayerLabels, usTaxPayerOptions } from "../../utils/utils";
import "react-datepicker/dist/react-datepicker.css";
import { idTypeOptions } from "../../utils/utils";

const IdentificationDetails = ({
  data,
  isEditable,
  refreshDetails,
  listStatus,
  countryOptions = [],
}) => {
  const [isEdit, setIsEdit] = useState(false);

  const [idNo, setIdNo] = useState(data?.govtIdNo || "");
  const [idType, setIdType] = useState(data?.govtIdType || "");
  const [citizenship, setCitizenship] = useState(data?.citizenship || "");
  const [country] = useState(data?.country || "");
  const [firstName] = useState(data?.firstName || "");
  const [middleName] = useState(data?.middleName || "");
  const [lastName] = useState(data?.lastName || "");
  const [dob] = useState(data?.dob ? new Date(data.dob) : "");
  const [usTaxPayer, setUsTaxPayer] = useState(data?.usTaxPayer || ""); 

  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isComment, setIsComment] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  const selectedCitizenship =
    countryOptions.find((opt) => opt.value === citizenship) || {};
  const citizenshipNames = countryOptions.map((opt) => opt.label);

  const handleCitizenshipChange = (selectedName) => {
    const selected = countryOptions.find((opt) => opt.label === selectedName);
    setCitizenship(selected ? selected.value : "");
  };

  const formatDate = (date) => {
    if (!date) return "";
    try {
      if (!(date instanceof Date)) date = new Date(date);
      return date.toISOString().split("T")[0];
    } catch {
      return "";
    }
  };

  const handleBackButton = () => {
    setIdNo(data?.govtIdNo || "");
    setIdType(data?.govtIdType || "");
    setCitizenship(data?.citizenship || "");
    setUsTaxPayer(data?.usTaxPayer || "");
    setIsEdit(false);
  };

  const handleSaveButton = () => {
    const citizenshipChanged = citizenship !== data?.citizenship;
    const idNoChanged = idNo !== data?.govtIdNo;

    if (citizenshipChanged) {
      setWarningMessage(
        "Citizenship is linked to partner, plan, address and national ID number settings. Review related configurations after update."
      );
      setIsWarningOpen(true);
    } else if (idNoChanged) {
      setWarningMessage(
        "Govt ID No is linked to user’s Identity (Name & DoB) settings. Review related configurations before update."
      );
      setIsWarningOpen(true);
    } else {
      setErrorMessage("");
      setError("");
      setSuccessMessage("");
      setComment("");
      setIsModalOpen(true);
    }
  };

  const handleToggleModal = () => {
    setErrorMessage("");
    setError("");
    setIsModalOpen(false);
  };

  const updateCustomerDetail = async (custId) => {
    if (loading) return;
    setLoading(true);

    const payload = {
      idNo,
      idType,
      citizenship,
      country,
      firstName,
      middleName,
      lastName,
      dob: formatDate(dob),
      usTaxPayer: usTaxPayer === "true" || usTaxPayer === true,
      comment: isComment,
    };

    try {
      const resp = await updateCustomer(custId, payload);
      if (resp?.code === 200) {
        setIsEdit(false);
        setSuccessMessage("Details updated successfully!");
        await refreshDetails();
        setTimeout(() => {
          setSuccessMessage("");
          setIsModalOpen(false);
        }, 3000);
      } else {
        setErrorMessage(resp?.data?.message || "Something went wrong");
        setTimeout(() => setIsModalOpen(false), 3000);
      }
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || "Failed to update details"
      );
      setTimeout(() => setIsModalOpen(false), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: "30px" }}>
      <Row className={styles.detailContainer}>
        <Col className="p-0">Identification Detail</Col>
        {isEditable && listStatus !== "CLOSED" ? (
          <Col
            md={2}
            onClick={() => setIsEdit(true)}
            style={{
              cursor: "pointer",
              display: "flex",
              justifyContent: "end",
            }}
          >
            <div style={{ width: "max-content" }}>
              {isEdit ? (
                <div style={{ display: "flex", flexDirection: "row" }}>
                  <Button
                    title="Save"
                    disabled={loading}
                    margin={"0px 10px"}
                    onClick={handleSaveButton}
                  />
                  <Button
                    title="Back"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBackButton();
                    }}
                  />
                </div>
              ) : (
                "Edit"
              )}
            </div>
          </Col>
        ) : null}
      </Row>

      {isEdit ? (
        <>
          <Row style={{ marginTop: 10 }}>
            <Col className={styles.text}>
              <DropDown
                label="Govt ID Type"
                name="idType"
                value={idType}
                data={idTypeOptions}
                onChange={(val) => setIdType(val)}
                placeholder="Select ID Type"
                formatValue={(val) => val}
              />
            </Col>
            <Col className={styles.text}>
              <Input
                label="Govt ID No"
                value={idNo}
                onChange={(e) => setIdNo(e.target.value)}
              />
            </Col>
            <Col className={styles.text}>
              <DropDown
                label="Citizenship"
                name="citizenship"
                value={selectedCitizenship.label}
                data={citizenshipNames}
                onChange={handleCitizenshipChange}
                placeholder="Select Citizenship"
              />
            </Col>
          </Row>

          <Row style={{ marginTop: 10 }}>
            <Col className={styles.text}>
              <DropDown
                label="US Tax Payer?"
                value={usTaxPayer}
                data={usTaxPayerOptions}
                onChange={(val) => setUsTaxPayer(val)}
                placeholder="Select US Tax Payer"
              />
            </Col>
            <Col className={styles.text}>
              {/* <Input
                label={"Country"}
                value={
                  countryOptions.find((opt) => opt.value === country)?.label ||
                  "--"
                }
                disabled
              /> */}
            </Col>
            <Col className={styles.text}>
              {/* <Input label={"DOB"} value={formatDate(dob)} disabled /> */}
            </Col>
          </Row>

          {/* <Row style={{ marginTop: 10 }}>
            <Col className={styles.text}>
              <Input label="First Name" value={firstName} disabled />
            </Col>
            <Col className={styles.text}>
              <Input
                label="Middle Name"
                value={middleName}
                disabled
              />
            </Col>
            <Col className={styles.text}>
              <Input label="Last Name" value={lastName} disabled />
            </Col>
          </Row> */}
        </>
      ) : (
        <>
          <Row style={{ marginTop: 10 }}>
            <Col className={styles.text}>Govt ID Type: {idType || "--"}</Col>
            <Col className={styles.text}>Govt ID No: {idNo || "--"}</Col>
            <Col className={styles.text}>
              Citizenship:{" "}
              {countryOptions.find((opt) => opt.value === citizenship)?.label ||"--"}
            </Col>
          </Row>
          <Row style={{ marginTop: 10 }}>
            <Col className={styles.text}>US Tax Payer? : {usTaxPayerLabels[usTaxPayer] || "--"}{" "}</Col>
            {/* <Col className={styles.text}>First Name: {firstName || "--"}</Col>
            <Col className={styles.text}>Middle Name: {middleName || "--"}</Col> */}
          </Row>
          {/* <Row style={{ marginTop: 10 }}>
            <Col className={styles.text}>Last Name: {lastName || "--"}</Col>
            <Col className={styles.text}>
              Country:{" "}
              {countryOptions.find((opt) => opt.value === country)?.label ||
                "--"}
            </Col>
            <Col className={styles.text}>DOB: {formatDate(dob) || "--"}</Col>
          </Row> */}
        </>
      )}

      <Modal
        isOpen={isWarningOpen}
        onRequestClose={() => setIsWarningOpen(false)}
        title="Warning"
        description={warningMessage}
        onYes={() => {
          setIsWarningOpen(false);
          setErrorMessage("");
          setError("");
          setSuccessMessage("");
          setComment("");
          setIsModalOpen(true);
        }}
        onNo={() => setIsWarningOpen(false)}
        yesLabel="Continue"
        noLabel="Cancel"
        showIcon={true}
      />

      <CommentModal
        setIsComment={setIsComment}
        isOpen={isModalOpen}
        onRequestClose={handleToggleModal}
        successMessage={successMessage}
        setSuccessMessage={setSuccessMessage}
        errorMessage={errorMessage}
        error={error}
        setError={setError}
        comment={comment}
        setComment={setComment}
        onSave={() => updateCustomerDetail(data.customerId)}
        loading={loading}
      />
    </div>
  );
};

export default IdentificationDetails;
